import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Box,
  LinearProgress,
  IconButton,
  Divider,
  Collapse,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Flag as ObjectiveIcon,
  ExpandMore,
  ExpandLess,
  Person as PersonIcon,
  AttachMoney,
  TrendingUp,
  Link as LinkIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import { STEP_LABELS } from '../utils/constants';
import useAISuggest from '../hooks/useAISuggest';

const ObjectiveCard = ({ objective, index, onEdit, onDelete, objectives }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  // Calculate overall progress from key results
  const keyResults = objective.key_results || [];
  const avgProgress = keyResults.length > 0 
    ? keyResults.reduce((sum, kr) => sum + (kr.progress || 0), 0) / keyResults.length 
    : 0;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
            {objective.name || 'Untitled Objective'}
          </Typography>
          <Chip 
            label={objective.priority || 'Medium'} 
            color={getPriorityColor(objective.priority)} 
            size="small" 
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {objective.description || 'No description provided'}
        </Typography>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Progress</Typography>
            <Typography variant="caption" fontWeight="bold">{Math.round(avgProgress)}%</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={avgProgress} 
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        {/* Owner */}
        {objective.owner && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Owner: {objective.owner}
            </Typography>
          </Box>
        )}

        {/* Budget */}
        {objective.budget && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AttachMoney fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Budget: ${objective.budget.toLocaleString()}
            </Typography>
          </Box>
        )}

        {/* Dependencies */}
        {objective.dependencies && objective.dependencies.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <LinkIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">Dependencies:</Typography>
            {objective.dependencies.map((depIndex, i) => (
              <Chip 
                key={i} 
                label={objectives[depIndex]?.name || `Obj ${depIndex + 1}`}
                size="small" 
                variant="outlined" 
              />
            ))}
          </Box>
        )}

        {/* Key Results Section */}
        {keyResults.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ textTransform: 'none', p: 0 }}
              >
                <Typography variant="caption" fontWeight="bold">
                  Key Results ({keyResults.length})
                </Typography>
              </Button>
              <Collapse in={expanded}>
                <Stack spacing={1} mt={1}>
                  {keyResults.map((kr, idx) => (
                    <Box key={idx} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" fontWeight="medium">
                        {kr.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={kr.progress || 0} 
                          sx={{ flex: 1, height: 4, borderRadius: 2 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {kr.progress || 0}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Target: {kr.target}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          </>
        )}

        {objective.target_date && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('stepContent.objectives.card.targetDate', { date: objective.target_date })}
            </Typography>
          </Box>
        )}
      </CardContent>
      
      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(index)}>
          {t('common.edit')}
        </Button>
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(index)}>
          {t('common.delete')}
        </Button>
      </CardActions>
    </Card>
  );
};

const ObjectiveForm = ({ objective, onSave, onCancel, existingObjectives = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: objective?.name || '',
    description: objective?.description || '',
    metrics: objective?.metrics || '',
    priority: objective?.priority || 'Medium',
    target_date: objective?.target_date || '',
    owner: objective?.owner || '',
    budget: objective?.budget || '',
    dependencies: objective?.dependencies || [],
    key_results: objective?.key_results || []
  });

  const [newKeyResult, setNewKeyResult] = useState({ description: '', target: '', progress: 0 });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave({
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null
      });
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleAddKeyResult = () => {
    if (newKeyResult.description.trim() && newKeyResult.target.trim()) {
      setFormData({
        ...formData,
        key_results: [...formData.key_results, { ...newKeyResult }]
      });
      setNewKeyResult({ description: '', target: '', progress: 0 });
    }
  };

  const handleDeleteKeyResult = (index) => {
    setFormData({
      ...formData,
      key_results: formData.key_results.filter((_, i) => i !== index)
    });
  };

  const handleUpdateKeyResultProgress = (index, progress) => {
    const updated = [...formData.key_results];
    updated[index].progress = progress;
    setFormData({ ...formData, key_results: updated });
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 2.5, md: 3 } }}>
      <Typography variant="h6" gutterBottom>
        {objective ? t('stepContent.objectives.form.editTitle') : t('stepContent.objectives.form.addTitle')}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label={t('stepContent.objectives.form.fields.name')}
              value={formData.name}
              onChange={handleChange('name')}
              placeholder={t('stepContent.objectives.form.placeholders.name')}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.objectives.form.fields.priority')}</InputLabel>
              <Select
                value={formData.priority}
                onChange={handleChange('priority')}
                label={t('stepContent.objectives.form.fields.priority')}
              >
                <MenuItem value="High">{t('priority.high')}</MenuItem>
                <MenuItem value="Medium">{t('priority.medium')}</MenuItem>
                <MenuItem value="Low">{t('priority.low')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.objectives.form.fields.description')}
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={t('stepContent.objectives.form.placeholders.description')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Owner/Responsible Person"
              value={formData.owner}
              onChange={handleChange('owner')}
              placeholder="e.g., John Smith, Marketing Team"
              InputProps={{
                startAdornment: <PersonIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Budget Allocation"
              type="number"
              value={formData.budget}
              onChange={handleChange('budget')}
              placeholder="0"
              InputProps={{
                startAdornment: <AttachMoney fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('stepContent.objectives.form.fields.targetDate')}
              type="date"
              value={formData.target_date}
              onChange={handleChange('target_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Dependencies</InputLabel>
              <Select
                multiple
                value={formData.dependencies}
                onChange={(e) => setFormData({ ...formData, dependencies: e.target.value })}
                label="Dependencies"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={existingObjectives[value]?.name || `Obj ${value + 1}`}
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
              >
                {existingObjectives.map((obj, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {obj.name || `Objective ${idx + 1}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Key Results Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp />
              Key Results (OKR)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Define 3-5 measurable outcomes that indicate achievement of this objective
            </Typography>

            <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Key Result Description"
                  placeholder="e.g., Increase monthly revenue to $50,000"
                  value={newKeyResult.description}
                  onChange={(e) => setNewKeyResult({ ...newKeyResult, description: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Target/Metric"
                  placeholder="e.g., $50,000/month"
                  value={newKeyResult.target}
                  onChange={(e) => setNewKeyResult({ ...newKeyResult, target: e.target.value })}
                />
                <Box>
                  <Typography variant="caption" gutterBottom>
                    Initial Progress: {newKeyResult.progress}%
                  </Typography>
                  <Slider
                    value={newKeyResult.progress}
                    onChange={(e, val) => setNewKeyResult({ ...newKeyResult, progress: val })}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Button
                  variant="outlined"
                  onClick={handleAddKeyResult}
                  disabled={!newKeyResult.description.trim() || !newKeyResult.target.trim()}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Add Key Result
                </Button>
              </Stack>
            </Card>

            {/* List of Key Results */}
            <Stack spacing={1}>
              {formData.key_results.map((kr, index) => (
                <Card key={index} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {kr.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Target: {kr.target}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">Progress</Typography>
                            <Typography variant="caption" fontWeight="bold">{kr.progress}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={kr.progress} 
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                          <Slider
                            value={kr.progress}
                            onChange={(e, val) => handleUpdateKeyResultProgress(index, val)}
                            min={0}
                            max={100}
                            step={5}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </Box>
                      <IconButton 
                        onClick={() => handleDeleteKeyResult(index)} 
                        color="error" 
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>
          
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
              sx={{ '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!formData.name.trim()}
              >
                {objective ? t('common.update') : t('common.add')}
              </Button>
              <Button onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default function ObjectivesPage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('strategic_objectives');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.strategic_objectives);

  const stepData = getStepData('strategic_objectives');
  const [objectives, setObjectives] = useState(stepData.objectives || []);
  const [isComplete, setIsComplete] = useState(isStepCompleted('strategic_objectives'));
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    const data = getStepData('strategic_objectives');
    setObjectives(data.objectives || []);
    setIsComplete(isStepCompleted('strategic_objectives'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = (newObjectives) => {
    setObjectives(newObjectives);
    updateStepData('strategic_objectives', {
      objectives: newObjectives
    });
  };

  const handleSaveObjective = (objectiveData) => {
    if (editingIndex !== null) {
      const updated = objectives.map((obj, index) => 
        index === editingIndex ? objectiveData : obj
      );
      updateData(updated);
    } else {
      updateData([...objectives, objectiveData]);
    }
    
    setShowForm(false);
    setEditingIndex(null);
  };

  const handleEditObjective = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDeleteObjective = (index) => {
    if (window.confirm(t('common.confirmDelete'))) {
      const updated = objectives.filter((_, i) => i !== index);
      updateData(updated);
    }
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('strategic_objectives', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = objectives.length > 0;

  return (
    <PageContainer maxWidth={1200}>
      <StepPageHeader
        stepNum={3}
        title={t(STEP_LABELS.strategic_objectives)}
        intro={t('stepContent.objectives.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.objectives.alerts.requireItem')}
      onAISuggest={handleAISuggest}
        />
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => {
          setEditingIndex(null);
          setShowForm(true);
        }}
        sx={{ mb: 3 }}
      >
        {t('stepContent.objectives.actions.addObjective')}
      </Button>

      {showForm && (
        <Stack mb={3}>
          <ObjectiveForm
            objective={editingIndex !== null ? objectives[editingIndex] : null}
            existingObjectives={objectives}
            onSave={handleSaveObjective}
            onCancel={() => {
              setShowForm(false);
              setEditingIndex(null);
            }}
          />
        </Stack>
      )}

      {objectives.length > 0 && (
        <Paper elevation={2} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <ObjectiveIcon />
            <Typography variant="h5">
              {t('stepContent.objectives.sections.listTitle', { count: objectives.length })}
            </Typography>
          </Stack>
          
          <Grid container spacing={3}>
            {objectives.map((objective, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <ObjectiveCard
                  objective={objective}
                  index={index}
                  objectives={objectives}
                  onEdit={handleEditObjective}
                  onDelete={handleDeleteObjective}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {objectives.length === 0 && !showForm && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('stepContent.objectives.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('stepContent.objectives.empty.description')}
          </Typography>
        </Paper>
      )}
    </PageContainer>
  );
}
